import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Typography, Box, useMediaQuery, useTheme, Grid, FormControl, CircularProgress } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { SelectField } from '../../../../../Components/Field';
import { TypedFormFieldValue } from '../../../../../Components/Form/Form.types';
import { useStyles } from '../../../../../Components/Field/Field.styles';
import { SelectOption } from '../../../../../Components/Form/Form.types';
import { StepButtonBar } from '../EditLegalListWizard/StepButtonBar';
import { StepProps } from './types';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useAptorApi } from '../../../../../Api';
import UpdateConnectionInfo from './UpdateConnectionInfo';

interface Step3LawExistingCompanyUnitSelectProps extends StepProps {
  lawId?: number;
  selectedCompanyUnit?: SelectOption;
  onCompanyUnitSelect: (unit: SelectOption) => void;
  currentStepName: string;
  updateConnectionInfo: any;
  setUpdateConnectionInfo: (info: any) => void;
}

const Step3LawExistingCompanyUnitSelect = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  lawId,
  selectedCompanyUnit,
  onCompanyUnitSelect,
  currentStepName,
    updateConnectionInfo,
  setUpdateConnectionInfo,
}: Step3LawExistingCompanyUnitSelectProps) => {
  const [localSelected, setLocalSelected] = useState<SelectOption | undefined>(selectedCompanyUnit);
  const [companyUnitOptions, setCompanyUnitOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { formatMessage } = useIntl();
  const { getCompanyUnitName } = useContext(UserContext);
  const { api, abortController } = useAptorApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(8);
  const classes = useStyles();

  useEffect(() => {
    if (lawId) {
      setIsLoading(true);
      api
        .searchLawCompanyUnits(lawId, { orderBy: 'name', orderDirection: 0, page: 0, pageSize: 100, search: '' })
        .then((result: any) => {
          if (!abortController.current.signal.aborted) {
            const units = result?.data || [];
            const options = (Array.isArray(units) ? units : []).map((u: any) => ({ label: u.name, value: u.id }));
            setCompanyUnitOptions(options);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          if (!abortController.current.signal.aborted) {
            console.error('Failed to load company units:', error);
            setIsLoading(false);
          }
        });
    }
  }, [lawId, api, abortController]);

  const handleChange = useCallback((val: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
    if (val && !Array.isArray(val)) {
      setLocalSelected(val as SelectOption);
    } else {
      setLocalSelected(undefined);
    }
  }, []);

  const handleNext = () => {
    if (localSelected) {
      onCompanyUnitSelect(localSelected);
      onNext();
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '350px',
          }}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Box style={{ maxWidth: '320px', width: '100%' }}>
              {/* <Typography variant="body2" style={{ marginBottom: theme.spacing(2), fontWeight: 600 }}>
                {`Select ${getCompanyUnitName(formatMessage)}`}
              </Typography> */}
              <Grid container spacing={3} justifyContent="flex-start">
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                  <Box style={{ maxWidth: '100%', width: '100%', minWidth: '320px' }}>
                    <FormControl className={classes.formControl} style={{ width: '100%' }}>
                      <SelectField
                        name="companyUnit"
                        placeholder={`Select a ${getCompanyUnitName(formatMessage)}`}
                        options={companyUnitOptions}
                        isMulti={false}
                        errorTexts={[]}
                        initialState={localSelected}
                        onChange={handleChange}
                      />
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </div>
        <UpdateConnectionInfo
        updateConnectionInfo={updateConnectionInfo}
        currentStepName={currentStepName}
        onUpdate={setUpdateConnectionInfo}
      />
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleNext}
          nextLabel="form.next"
          nextDisabled={!localSelected}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />
      </Box>
    </div>
  );
};

export default Step3LawExistingCompanyUnitSelect;
